import katex from 'katex';
import { Boxes, ImageIcon, Plus, Sparkles, Trash2, Upload, Video } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { UploadProgressBar } from '@/components/ui/UploadProgressBar';
import type {
  Asset,
  ChipContent,
  ElementPosition,
  ElementType,
  EmbedContent,
  ImageContent,
  LatexContent,
  McqContent,
  ScriptElement,
  ShapeContent,
  ThreeDContent,
  TimelineContent,
  TimelineItem,
  VideoContent,
} from '@/lib/schema.types';

import { api } from '@/lib/api';
import {
  CALLOUT_BODY_PLACEHOLDERS,
  CALLOUT_VARIANT_LABELS,
  CALLOUT_VARIANTS,
} from '@/lib/callout';
import { searchIcons } from '@/lib/icon-library';
import {
  DEFAULT_MERMAID_SOURCE,
  MERMAID_DIAGRAM_TYPES,
  withDiagramType,
} from '@/lib/mermaid-types';
import type { CalloutVariant, MermaidDiagramType } from '@/lib/schema.types';
import { uploadFile } from '@/lib/uploads';
import { cn, uid } from '@/lib/utils';
import { useEditorStore } from '@/store/editor.store';

import { LatexElement } from './elements/LatexElement';
import { MermaidElement } from './elements/MermaidElement';

export interface PendingInsertion {
  elementType: ElementType;
  target: { slideId: string; rowId: string; columnId: string };
  index?: number;
  /**
   * When the drop happened outside any existing column we created a fresh row
   * to host the new element. If the user cancels the widget we roll that row
   * back so we don't leave an empty layout behind.
   */
  createdRow?: boolean;
  /**
   * For freeform rows: the position (relative to the column) where the user
   * dropped the block. Applied to the created element when set.
   */
  position?: ElementPosition;
}

interface Props {
  pending: PendingInsertion | null;
  onCancel: () => void;
  onConfirm: (element: ScriptElement) => void;
}

const TITLES: Record<
  ElementType,
  { title: string; description: string }
> = {
  text: { title: 'Add Text Content', description: 'Heading, paragraph, or callout copy.' },
  image: { title: 'Add Image Asset', description: 'Paste a URL or upload from your device.' },
  video: { title: 'Add Video Player', description: 'MP4 URL or hosted video link.' },
  mcq: { title: 'Add Multiple Choice', description: 'Author a question with answer options.' },
  chip: { title: 'Add Chip / Tag', description: 'Compact label or category badge.' },
  shape: { title: 'Add Shape', description: 'Decorative geometry block.' },
  embed: {
    title: 'Add Embed / Simulation',
    description: 'Inline an external page or interactive simulation.',
  },
  '3d': { title: 'Add 3D Model', description: 'Placeholder for a WebGL / glTF asset.' },
  latex: {
    title: 'Add Math (LaTeX)',
    description: 'Type or paste a TeX / KaTeX expression with live preview.',
  },
  icon: { title: 'Add Icon', description: 'Pick a pedagogical glyph from the icon library.' },
  callout: {
    title: 'Add Callout',
    description: 'Highlight a mistake, memory trick, or key concept.',
  },
  mermaid: {
    title: 'Add Mermaid Diagram',
    description: 'Flowchart, mind map, timeline or sequence from text.',
  },
  timeline: {
    title: 'Add Timeline',
    description: 'Numbered events on a line, alternating above and below.',
  },
};

export function BlockCreator({ pending, onCancel, onConfirm }: Props) {
  // Safety net for SCHEMA_USAGE.md Rule 4 ("one quiz slide = one
  // question"). The primary guard lives in EditorDndProvider; this catches
  // any future entry path (context menus, slash commands, etc.).
  const blockedByQuizRule = useEditorStore((s) => {
    if (!pending || pending.elementType !== 'mcq') return false;
    if (s.mode !== 'pack') return false;
    const slide = s.script?.slides.find((sl) => sl.id === pending.target.slideId);
    return slide?.beatMeta?.kind === 'quiz';
  });

  if (!pending) return null;
  const meta = TITLES[pending.elementType];

  if (blockedByQuizRule) {
    return (
      <Modal
        open
        onClose={onCancel}
        title="Quiz slides hold one question"
        description="Each quiz slide is one adaptive checkpoint with one MCQ. To add another question, use the Quiz Slide card or the Add another question button — it chains a new quiz slide into the routing flow."
        footer={
          <Button variant="primary" onClick={onCancel}>
            Got it
          </Button>
        }
      >
        <p className="text-[13px] text-fg-muted">
          Each quiz slide holds one question. A second question added here
          wouldn&apos;t be scored — use “Add another question” to chain a new
          quiz slide instead.
        </p>
      </Modal>
    );
  }

  return (
    <Modal open onClose={onCancel} title={meta.title} description={meta.description}>
      <CreatorBody type={pending.elementType} onCancel={onCancel} onConfirm={onConfirm} />
    </Modal>
  );
}

function CreatorBody({
  type,
  onCancel,
  onConfirm,
}: {
  type: ElementType;
  onCancel: () => void;
  onConfirm: (element: ScriptElement) => void;
}) {
  switch (type) {
    case 'text':
      return <TextCreator onCancel={onCancel} onConfirm={onConfirm} />;
    case 'image':
      return <ImageCreator onCancel={onCancel} onConfirm={onConfirm} />;
    case 'timeline':
      return <TimelineCreator onCancel={onCancel} onConfirm={onConfirm} />;
    case 'video':
      return <VideoCreator onCancel={onCancel} onConfirm={onConfirm} />;
    case 'mcq':
      return <McqCreator onCancel={onCancel} onConfirm={onConfirm} />;
    case 'chip':
      return <ChipCreator onCancel={onCancel} onConfirm={onConfirm} />;
    case 'shape':
      return <ShapeCreator onCancel={onCancel} onConfirm={onConfirm} />;
    case 'embed':
      return <EmbedCreator onCancel={onCancel} onConfirm={onConfirm} />;
    case '3d':
      return <ThreeDCreator onCancel={onCancel} onConfirm={onConfirm} />;
    case 'latex':
      return <LatexCreator onCancel={onCancel} onConfirm={onConfirm} />;
    case 'icon':
      return <IconCreator onCancel={onCancel} onConfirm={onConfirm} />;
    case 'callout':
      return <CalloutCreator onCancel={onCancel} onConfirm={onConfirm} />;
    case 'mermaid':
      return <MermaidCreator onCancel={onCancel} onConfirm={onConfirm} />;
  }
}

// ---------------- shared bits ----------------

function FooterRow({
  onCancel,
  onConfirm,
  disabled,
  confirmLabel = 'Add to slide',
}: {
  onCancel: () => void;
  onConfirm: () => void;
  disabled?: boolean;
  confirmLabel?: string;
}) {
  return (
    <div className="-mx-5 -mb-4 flex items-center justify-end gap-2 border-t border-border-subtle bg-bg-subtle/40 px-5 py-3 mt-4">
      <Button variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
      <Button variant="primary" onClick={onConfirm} disabled={disabled}>
        {confirmLabel}
      </Button>
    </div>
  );
}

function PreviewCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-dashed border-border bg-bg-subtle/60 p-3">
      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-fg-subtle">
        Preview
      </div>
      <div className="rounded bg-white p-3 ring-1 ring-border">{children}</div>
    </div>
  );
}

// ---------------- TEXT ----------------

function TextCreator({ onCancel, onConfirm }: ChildProps) {
  const [text, setText] = useState('Building Great Products');
  const [size, setSize] = useState(40);
  const [weight, setWeight] = useState(700);
  const [color, setColor] = useState('#1a1a2e');

  const submit = () => {
    onConfirm({
      id: uid('el'),
      type: 'text',
      content: text,
      style: { fontSize: size, fontWeight: weight, color, textAlign: 'left' },
      handwritten: true,
    });
  };

  return (
    <div className="space-y-4">
      <label className="block">
        <span className="mb-1 block text-[11px] font-medium text-fg-muted">Text</span>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          autoFocus
          className="w-full rounded-md border border-border bg-white p-2.5 text-sm text-fg focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
        />
      </label>

      <div className="grid grid-cols-3 gap-2">
        <Input
          type="number"
          label="Size"
          value={size}
          onChange={(e) => setSize(Number(e.target.value) || 16)}
        />
        <Select
          label="Weight"
          value={String(weight)}
          onChange={(e) => setWeight(Number(e.target.value))}
        >
          <option value="300">Light</option>
          <option value="400">Regular</option>
          <option value="500">Medium</option>
          <option value="600">Semibold</option>
          <option value="700">Bold</option>
          <option value="800">Extra Bold</option>
        </Select>
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium text-fg-muted">Color</span>
          <div className="flex h-9 items-center gap-1.5 rounded-md border border-border bg-white pl-1.5 pr-2">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-6 w-7 cursor-pointer rounded-sm border border-border"
            />
            <input
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-full bg-transparent text-xs text-fg outline-none"
            />
          </div>
        </label>
      </div>

      <PreviewCard>
        <div style={{ fontSize: size, fontWeight: weight, color }}>{text || 'Preview text'}</div>
      </PreviewCard>

      <FooterRow onCancel={onCancel} onConfirm={submit} disabled={!text.trim()} />
    </div>
  );
}

// ---------------- IMAGE ----------------

type ImageTab = 'url' | 'upload' | 'generate' | 'stickers';

/** Animated GIF/WebP accents picked from the sticker library (`kind: 'gif'`).
 *  Inserted as ordinary image elements — <img> plays GIFs natively, and the
 *  publish asset manifest hashes them like any slide image. */
function StickerPicker({ onPick }: { onPick: (asset: Asset) => void }) {
  const [stickers, setStickers] = useState<Asset[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let on = true;
    api.assets
      .list({ kind: 'gif' })
      .then((list) => on && setStickers(list))
      .catch((e) => on && setError(e instanceof Error ? e.message : 'Failed to load stickers'));
    return () => {
      on = false;
    };
  }, []);

  if (error) return <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-600">{error}</p>;
  if (!stickers) return <p className="text-xs text-fg-muted">Loading sticker library…</p>;
  if (stickers.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border bg-bg-subtle/40 px-3 py-4 text-center text-xs text-fg-muted">
        No stickers yet — upload animated GIFs on the Assets page (Stickers tab).
      </p>
    );
  }
  return (
    <div className="grid max-h-64 grid-cols-4 gap-2 overflow-y-auto scroll-thin">
      {stickers.map((s) => (
        <button
          key={s._id}
          type="button"
          onClick={() => onPick(s)}
          title={s.name}
          className="flex aspect-square items-center justify-center rounded-md border border-border bg-white p-1.5 transition hover:border-accent hover:shadow-sm"
        >
          <img src={s.url} alt={s.name} className="max-h-full max-w-full object-contain" />
        </button>
      ))}
    </div>
  );
}

type GeminiAspect = '1:1' | '16:9' | '9:16' | '4:3' | '3:4';

const ASPECT_OPTIONS: { id: string; label: string; w: number; h: number; aspect: GeminiAspect }[] = [
  { id: 'square', label: 'Square (1:1)', w: 1024, h: 1024, aspect: '1:1' },
  { id: 'wide', label: 'Wide (16:9)', w: 1280, h: 720, aspect: '16:9' },
  { id: 'tall', label: 'Tall (9:16)', w: 768, h: 1366, aspect: '9:16' },
  { id: 'classic', label: 'Classic (4:3)', w: 1024, h: 768, aspect: '4:3' },
  { id: 'portrait', label: 'Portrait (3:4)', w: 768, h: 1024, aspect: '3:4' },
];

const STYLE_OPTIONS: { id: string; label: string }[] = [
  { id: '', label: 'No style preset' },
  { id: 'photorealistic, soft lighting, sharp focus', label: 'Photorealistic' },
  { id: 'flat vector illustration, clean lines, pastel palette', label: 'Vector illustration' },
  { id: 'watercolor painting, soft edges, paper texture', label: 'Watercolor' },
  { id: 'isometric 3D render, soft shadows, pastel palette', label: 'Isometric 3D' },
  { id: 'whiteboard sketch, hand-drawn, marker strokes', label: 'Whiteboard sketch' },
  { id: 'science textbook diagram, labeled, neutral background', label: 'Textbook diagram' },
];

function ImageCreator({ onCancel, onConfirm }: ChildProps) {
  const [tab, setTab] = useState<ImageTab>('url');
  const [src, setSrc] = useState('');
  const [alt, setAlt] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Generate-tab state
  const [prompt, setPrompt] = useState('');
  const [aspect, setAspect] = useState(ASPECT_OPTIONS[0].id);
  const [style, setStyle] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const onFile = async (file: File) => {
    setUploadError(null);
    setUploading(true);
    setUploadProgress(0);
    try {
      const uploaded = await uploadFile(file, {
        type: 'CONTENT',
        onProgress: setUploadProgress,
      });
      setSrc(uploaded.url);
      if (!alt) setAlt(stripExtensionForLabel(uploaded.name));
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const generate = async () => {
    if (!prompt.trim()) return;
    setGenerateError(null);
    setGenerating(true);
    try {
      const dim = ASPECT_OPTIONS.find((a) => a.id === aspect) ?? ASPECT_OPTIONS[0];
      const res = await api.ai.generateImage({
        prompt: prompt.trim(),
        width: dim.w,
        height: dim.h,
        aspectRatio: dim.aspect,
        style: style || undefined,
      });
      setSrc(res.url);
      if (!alt) setAlt(prompt.trim().slice(0, 120));
    } catch (err) {
      setGenerateError(err instanceof Error ? err.message : 'Image generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const submit = () => {
    if (!src) return;
    onConfirm({
      id: uid('el'),
      type: 'image',
      content: { src, alt } satisfies ImageContent,
      style: { borderRadius: 8 },
    });
  };

  return (
    <div className="space-y-4">
      <Tabs
        value={tab}
        onChange={(t) => setTab(t as ImageTab)}
        options={[
          { id: 'url', label: 'From URL' },
          { id: 'upload', label: 'Upload' },
          { id: 'generate', label: 'Generate' },
          { id: 'stickers', label: 'Stickers' },
        ]}
      />

      {tab === 'stickers' && (
        <StickerPicker
          onPick={(a) => {
            setSrc(a.url);
            if (!alt) setAlt(a.name);
          }}
        />
      )}

      {tab === 'url' && (
        <Input
          label="Image URL"
          value={src}
          autoFocus
          placeholder="https://…/picture.jpg"
          onChange={(e) => setSrc(e.target.value)}
        />
      )}

      {tab === 'upload' && (
        <div className="space-y-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onFile(f);
            }}
          />
          <button
            type="button"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            className="flex h-32 w-full flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border bg-bg-subtle/40 text-fg-muted transition hover:border-accent/60 hover:text-accent disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Upload size={20} />
            <span className="text-sm font-medium">
              {uploading ? 'Uploading…' : 'Click to upload an image'}
            </span>
            <span className="text-[11px] text-fg-subtle">
              PNG, JPG, GIF, WebP — uploaded to S3
            </span>
          </button>
          {uploading && <UploadProgressBar value={uploadProgress} />}
          {uploadError && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-600">{uploadError}</p>
          )}
        </div>
      )}

      {tab === 'generate' && (
        <div className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium text-fg-muted">Prompt</span>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={3}
              autoFocus
              placeholder="A cross-section of a plant cell on a white background, labeled"
              className="w-full rounded-md border border-border bg-white p-2.5 text-sm text-fg focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <Select label="Aspect" value={aspect} onChange={(e) => setAspect(e.target.value)}>
              {ASPECT_OPTIONS.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}
                </option>
              ))}
            </Select>
            <Select label="Style" value={style} onChange={(e) => setStyle(e.target.value)}>
              {STYLE_OPTIONS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </Select>
          </div>
          <Button
            variant="primary"
            className="w-full justify-center"
            onClick={generate}
            disabled={!prompt.trim() || generating}
          >
            <Sparkles size={14} /> {generating ? 'Generating…' : src ? 'Regenerate image' : 'Generate image'}
          </Button>
          {generating && (
            <p className="text-[11px] text-fg-subtle">
              Calling the image provider… this can take 5-15s for high-res prompts.
            </p>
          )}
          {generateError && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-600">{generateError}</p>
          )}
        </div>
      )}

      <Input
        label="Alt text (optional)"
        value={alt}
        onChange={(e) => setAlt(e.target.value)}
        placeholder="Describes the image for accessibility"
      />

      <PreviewCard>
        {src ? (
          <img src={src} alt={alt} className="block max-h-48 w-full rounded object-contain" />
        ) : (
          <div className="flex h-32 items-center justify-center text-xs text-fg-subtle">
            <ImageIcon size={20} />
          </div>
        )}
      </PreviewCard>

      <FooterRow onCancel={onCancel} onConfirm={submit} disabled={!src} />
    </div>
  );
}

// ---------------- VIDEO ----------------

function VideoCreator({ onCancel, onConfirm }: ChildProps) {
  const [tab, setTab] = useState<'url' | 'upload'>('url');
  const [src, setSrc] = useState('');
  const [poster, setPoster] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const onFile = async (file: File) => {
    setError(null);
    setUploading(true);
    setProgress(0);
    try {
      const uploaded = await uploadFile(file, {
        type: 'CONTENT',
        onProgress: setProgress,
      });
      setSrc(uploaded.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const submit = () => {
    if (!src) return;
    onConfirm({
      id: uid('el'),
      type: 'video',
      content: { src, poster: poster || undefined } satisfies VideoContent,
      style: { borderRadius: 8 },
    });
  };

  return (
    <div className="space-y-4">
      <Tabs
        value={tab}
        onChange={(t) => setTab(t as 'url' | 'upload')}
        options={[
          { id: 'url', label: 'From URL' },
          { id: 'upload', label: 'Upload' },
        ]}
      />

      {tab === 'url' ? (
        <Input
          label="Video URL (mp4 / webm)"
          value={src}
          autoFocus
          placeholder="https://…/video.mp4"
          onChange={(e) => setSrc(e.target.value)}
        />
      ) : (
        <div className="space-y-2">
          <input
            ref={fileRef}
            type="file"
            accept="video/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onFile(f);
            }}
          />
          <button
            type="button"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            className="flex h-32 w-full flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border bg-bg-subtle/40 text-fg-muted transition hover:border-accent/60 hover:text-accent disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Upload size={20} />
            <span className="text-sm font-medium">
              {uploading ? 'Uploading…' : 'Click to upload a video'}
            </span>
            <span className="text-[11px] text-fg-subtle">
              MP4, WebM, MOV — uploaded to S3
            </span>
          </button>
          {uploading && <UploadProgressBar value={progress} />}
          {error && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-600">{error}</p>
          )}
        </div>
      )}

      <Input
        label="Poster URL (optional)"
        value={poster}
        placeholder="https://…/poster.jpg"
        onChange={(e) => setPoster(e.target.value)}
      />
      <PreviewCard>
        {src ? (
          <video src={src} poster={poster || undefined} controls className="w-full rounded" />
        ) : (
          <div className="flex h-32 items-center justify-center text-xs text-fg-subtle">
            <Video size={20} />
          </div>
        )}
      </PreviewCard>
      <FooterRow onCancel={onCancel} onConfirm={submit} disabled={!src} />
    </div>
  );
}

// ---------------- MCQ ----------------

function McqCreator({ onCancel, onConfirm }: ChildProps) {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState<string[]>(['', '', '', '']);
  const [answerIndex, setAnswerIndex] = useState(0);

  const setOpt = (i: number, v: string) =>
    setOptions((prev) => prev.map((o, ix) => (ix === i ? v : o)));

  const addOpt = () => options.length < 6 && setOptions((prev) => [...prev, '']);
  const removeOpt = (i: number) => {
    if (options.length <= 2) return;
    setOptions((prev) => prev.filter((_, ix) => ix !== i));
    if (answerIndex >= options.length - 1) setAnswerIndex(0);
  };

  const submit = () => {
    onConfirm({
      id: uid('el'),
      type: 'mcq',
      content: {
        question: question.trim(),
        options: options.map((o) => o.trim()),
        answerIndex,
      } satisfies McqContent,
      style: { fontSize: 16, color: '#111827' },
    });
  };

  const valid = question.trim().length > 0 && options.every((o) => o.trim().length > 0);

  return (
    <div className="space-y-4">
      <Input
        label="Question"
        value={question}
        autoFocus
        placeholder="What is the core principle of MVP?"
        onChange={(e) => setQuestion(e.target.value)}
      />
      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[11px] font-medium text-fg-muted">Options</span>
          <button
            onClick={addOpt}
            disabled={options.length >= 6}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-accent hover:underline disabled:opacity-40"
          >
            <Plus size={11} /> Add option
          </button>
        </div>
        <div className="space-y-1.5">
          {options.map((opt, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="radio"
                checked={answerIndex === i}
                onChange={() => setAnswerIndex(i)}
                title="Mark as correct answer"
              />
              <span className="grid h-5 w-5 place-items-center rounded-full bg-bg-subtle text-[10px] font-medium text-fg-muted">
                {String.fromCharCode(65 + i)}
              </span>
              <input
                value={opt}
                onChange={(e) => setOpt(i, e.target.value)}
                placeholder={`Option ${String.fromCharCode(65 + i)}`}
                className="h-9 flex-1 rounded-md border border-border bg-white px-2.5 text-sm text-fg focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
              />
              <button
                onClick={() => removeOpt(i)}
                disabled={options.length <= 2}
                className="grid h-7 w-7 place-items-center rounded-md text-fg-subtle hover:bg-bg-subtle hover:text-red-500 disabled:opacity-40"
                title="Remove option"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
        <p className="mt-1.5 text-[11px] text-fg-subtle">
          The selected radio marks the correct answer.
        </p>
      </div>
      <FooterRow onCancel={onCancel} onConfirm={submit} disabled={!valid} />
    </div>
  );
}

// ---------------- CHIP ----------------

function TimelineCreator({ onCancel, onConfirm }: ChildProps) {
  const [items, setItems] = useState<TimelineItem[]>([
    { id: uid('tl'), label: '1665', body: '' },
    { id: uid('tl'), label: '1670s', body: '' },
  ]);
  const patch = (i: number, p: Partial<TimelineItem>) =>
    setItems((prev) => prev.map((it, k) => (k === i ? { ...it, ...p } : it)));

  const submit = () => {
    onConfirm({
      id: uid('el'),
      type: 'timeline',
      content: { items } satisfies TimelineContent,
      style: { fontSize: 16 },
      // A timeline wants width — start wide instead of the default small box.
      position: { x: 90, y: 160, w: 1100, h: 360 },
    });
  };

  return (
    <div className="space-y-4">
      <div className="max-h-72 space-y-3 overflow-y-auto scroll-thin pr-1">
        {items.map((it, i) => (
          <div key={it.id} className="rounded-md border border-border-subtle bg-bg-subtle/40 p-2">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-fg-subtle">
                Event {i + 1} — {i % 2 === 0 ? 'above' : 'below'} the line
              </span>
              {items.length > 2 && (
                <button
                  type="button"
                  onClick={() => setItems((prev) => prev.filter((_, k) => k !== i))}
                  className="text-fg-subtle hover:text-red-500"
                  title="Remove event"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
            <Input
              label="Label (bold)"
              value={it.label}
              onChange={(e) => patch(i, { label: e.target.value })}
            />
            <label className="mt-1.5 block">
              <span className="mb-1 block text-[11px] font-medium text-fg-muted">Body</span>
              <textarea
                value={it.body ?? ''}
                rows={2}
                onChange={(e) => patch(i, { body: e.target.value })}
                className="block w-full resize-y rounded-md border border-border bg-white px-2 py-1.5 text-sm text-fg outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
              />
            </label>
          </div>
        ))}
      </div>
      <Button
        variant="soft"
        onClick={() => setItems((prev) => [...prev, { id: uid('tl'), label: '', body: '' }])}
      >
        <Plus size={13} /> Add event
      </Button>
      <FooterRow
        onCancel={onCancel}
        onConfirm={submit}
        disabled={!items.some((i) => i.label.trim())}
      />
    </div>
  );
}

function ChipCreator({ onCancel, onConfirm }: ChildProps) {
  const [label, setLabel] = useState('New chip');
  const [bg, setBg] = useState('#e0e7ff');
  const [color, setColor] = useState('#1f2937');

  const submit = () => {
    onConfirm({
      id: uid('el'),
      type: 'chip',
      content: { label } satisfies ChipContent,
      style: { backgroundColor: bg, color, fontSize: 13, borderRadius: 999 },
    });
  };

  return (
    <div className="space-y-4">
      <Input label="Label" autoFocus value={label} onChange={(e) => setLabel(e.target.value)} />
      <div className="grid grid-cols-2 gap-2">
        <ColorField label="Background" value={bg} onChange={setBg} />
        <ColorField label="Text color" value={color} onChange={setColor} />
      </div>
      <PreviewCard>
        <span
          className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium"
          style={{ backgroundColor: bg, color }}
        >
          {label || 'Chip'}
        </span>
      </PreviewCard>
      <FooterRow onCancel={onCancel} onConfirm={submit} disabled={!label.trim()} />
    </div>
  );
}

// ---------------- SHAPE ----------------

const SHAPE_OPTIONS: { kind: ShapeContent['shape']; label: string }[] = [
  { kind: 'rect', label: 'Rectangle' },
  { kind: 'circle', label: 'Circle' },
  { kind: 'ellipse', label: 'Ellipse' },
  { kind: 'triangle', label: 'Triangle' },
  { kind: 'diamond', label: 'Diamond' },
  { kind: 'pentagon', label: 'Pentagon' },
  { kind: 'hexagon', label: 'Hexagon' },
  { kind: 'star', label: 'Star' },
  { kind: 'arrow', label: 'Arrow' },
  { kind: 'heart', label: 'Heart' },
  { kind: 'cross', label: 'Cross' },
  { kind: 'line', label: 'Line' },
];

function ShapeCreator({ onCancel, onConfirm }: ChildProps) {
  const [shape, setShape] = useState<ShapeContent['shape']>('rect');
  const [fill, setFill] = useState('#6366f1');

  const submit = () => {
    onConfirm({
      id: uid('el'),
      type: 'shape',
      content: { shape, fill } satisfies ShapeContent,
      style: { backgroundColor: fill, borderRadius: 8 },
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <span className="mb-1 block text-[11px] font-medium text-fg-muted">Shape</span>
        <div className="grid grid-cols-4 gap-2">
          {SHAPE_OPTIONS.map(({ kind, label }) => (
            <button
              key={kind}
              onClick={() => setShape(kind)}
              className={cn(
                'flex h-16 flex-col items-center justify-center gap-1.5 rounded-md border bg-white text-[10px] font-medium text-fg-muted transition',
                shape === kind
                  ? 'border-accent text-accent ring-2 ring-accent/20'
                  : 'border-border hover:border-accent/40',
              )}
              title={label}
            >
              <ShapePreview shape={kind} fill={fill} />
              {label}
            </button>
          ))}
        </div>
      </div>
      <ColorField label="Fill color" value={fill} onChange={setFill} />
      <FooterRow onCancel={onCancel} onConfirm={submit} />
    </div>
  );
}

function ShapePreview({ shape, fill }: { shape: ShapeContent['shape']; fill: string }) {
  if (shape === 'circle')
    return <span className="block h-6 w-6 rounded-full" style={{ background: fill }} />;
  if (shape === 'ellipse')
    return <span className="block h-3 w-7 rounded-full" style={{ background: fill }} />;
  if (shape === 'line')
    return <span className="block h-1 w-8 rounded-full" style={{ background: fill }} />;
  if (shape === 'rect')
    return <span className="block h-5 w-7 rounded-sm" style={{ background: fill }} />;
  if (shape === 'triangle')
    return (
      <span
        style={{
          width: 0,
          height: 0,
          borderLeft: '10px solid transparent',
          borderRight: '10px solid transparent',
          borderBottom: `16px solid ${fill}`,
        }}
      />
    );
  return <SvgShapePreview shape={shape} fill={fill} />;
}

function SvgShapePreview({
  shape,
  fill,
}: {
  shape: Exclude<ShapeContent['shape'], 'rect' | 'circle' | 'ellipse' | 'triangle' | 'line'>;
  fill: string;
}) {
  const geometry = (() => {
    switch (shape) {
      case 'diamond':
        return { kind: 'polygon' as const, data: '50,4 96,50 50,96 4,50' };
      case 'pentagon':
        return { kind: 'polygon' as const, data: '50,4 96,38 78,94 22,94 4,38' };
      case 'hexagon':
        return { kind: 'polygon' as const, data: '25,6 75,6 96,50 75,94 25,94 4,50' };
      case 'star':
        return {
          kind: 'polygon' as const,
          data: '50,4 61,38 96,38 68,58 79,92 50,72 21,92 32,58 4,38 39,38',
        };
      case 'arrow':
        return {
          kind: 'polygon' as const,
          data: '4,34 60,34 60,12 96,50 60,88 60,66 4,66',
        };
      case 'heart':
        return {
          kind: 'path' as const,
          data:
            'M50 88 C 14 60, 4 38, 18 22 C 30 8, 46 14, 50 28 C 54 14, 70 8, 82 22 C 96 38, 86 60, 50 88 Z',
        };
      case 'cross':
        return {
          kind: 'polygon' as const,
          data: '38,4 62,4 62,38 96,38 96,62 62,62 62,96 38,96 38,62 4,62 4,38 38,38',
        };
    }
  })();
  return (
    <svg viewBox="0 0 100 100" className="h-6 w-6" aria-hidden>
      {geometry.kind === 'polygon' ? (
        <polygon points={geometry.data} fill={fill} />
      ) : (
        <path d={geometry.data} fill={fill} />
      )}
    </svg>
  );
}

// ---------------- EMBED ----------------

function EmbedCreator({ onCancel, onConfirm }: ChildProps) {
  const [mode, setMode] = useState<'url' | 'html'>('url');
  const [url, setUrl] = useState('');
  const [html, setHtml] = useState('');
  const [height, setHeight] = useState(320);

  const ready = mode === 'url' ? !!url.trim() : !!html.trim();
  const submit = () => {
    if (!ready) return;
    onConfirm({
      id: uid('el'),
      type: 'embed',
      content: (mode === 'html' ? { html, height } : { url, height }) satisfies EmbedContent,
      style: { borderRadius: 8 },
    });
  };

  return (
    <div className="space-y-4">
      <Select label="Source" value={mode} onChange={(e) => setMode(e.target.value as 'url' | 'html')}>
        <option value="url">Website URL</option>
        <option value="html">HTML / simulation</option>
      </Select>
      {mode === 'url' ? (
        <Input
          label="Embed URL"
          autoFocus
          placeholder="https://…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
      ) : (
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium text-fg-muted">HTML</span>
          <textarea
            autoFocus
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            rows={10}
            spellCheck={false}
            placeholder="<!doctype html><html>…  (interactive simulation, runs sandboxed)"
            className="w-full rounded-md border border-border px-2 py-1 font-mono text-[11px] text-fg"
          />
        </label>
      )}
      <Input
        type="number"
        label="Height (px)"
        value={height}
        onChange={(e) => setHeight(Number(e.target.value) || 320)}
      />
      <p className="text-[11px] text-fg-subtle">
        The iframe is sandboxed (<code>allow-scripts allow-same-origin</code>); some sites may
        refuse to embed.
      </p>
      <FooterRow onCancel={onCancel} onConfirm={submit} disabled={!ready} />
    </div>
  );
}

// ---------------- 3D ----------------

function ThreeDCreator({ onCancel, onConfirm }: ChildProps) {
  const [tab, setTab] = useState<'library' | 'url' | 'upload'>('library');
  const [src, setSrc] = useState('');
  const [alt, setAlt] = useState('');
  const [autoRotate, setAutoRotate] = useState(true);
  const [filename, setFilename] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [progress, setProgress] = useState(0);
  // Carried from a library pick or a fresh upload so the inserted element keeps
  // provenance + the model's default ("home") camera pose.
  const [picked, setPicked] = useState<{
    modelId?: string;
    poster?: string;
    defaultCameraOrbit?: string;
    defaultCameraTarget?: string;
    fieldOfView?: string;
  }>({});

  const selectAsset = (asset: Asset) => {
    setSrc(asset.url);
    setAlt(asset.name);
    setFilename(asset.fileFormat === 'glb' ? '.glb' : '.gltf');
    setPicked({
      modelId: asset._id,
      poster: asset.poster ?? undefined,
      defaultCameraOrbit: asset.defaultCameraOrbit ?? undefined,
      defaultCameraTarget: asset.defaultCameraTarget ?? undefined,
    });
    setError(null);
  };

  const onFile = async (file: File) => {
    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.glb') && !lower.endsWith('.gltf')) {
      setError('Only .glb or .gltf files are supported.');
      return;
    }
    const fileFormat: 'glb' | 'gltf' = lower.endsWith('.glb') ? 'glb' : 'gltf';
    const contentType = fileFormat === 'glb' ? 'model/gltf-binary' : 'model/gltf+json';
    setError(null);
    setLoading(true);
    setProgress(0);
    setFilename(file.name);
    try {
      const uploaded = await uploadFile(file, {
        type: 'CONTENT',
        contentType,
        extension: fileFormat,
        onProgress: setProgress,
      });
      setSrc(uploaded.url);
      // Also save the upload into the reusable library (best-effort — a failure
      // here shouldn't block inserting the model onto the slide).
      try {
        const asset = await api.assets.create({
          name: stripExtensionForLabel(file.name),
          kind: 'model3d',
          url: uploaded.url,
          s3Key: uploaded.key,
          contentType,
          fileFormat,
          fileSize: file.size,
          tags: [],
        });
        setPicked({ modelId: asset._id });
      } catch (err) {
        // Non-fatal: the model uploaded fine, only the library registration
        // failed — the element still works, it just loses provenance.
        console.warn('3D model uploaded but library registration failed', err);
        setPicked({});
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const format: 'glb' | 'gltf' | undefined = (() => {
    const lower = (filename || src).toLowerCase();
    if (lower.includes('.glb')) return 'glb';
    if (lower.includes('.gltf')) return 'gltf';
    return undefined;
  })();

  const submit = () => {
    if (!src) return;
    onConfirm({
      id: uid('el'),
      type: '3d',
      content: {
        src,
        alt: alt || undefined,
        autoRotate,
        cameraControls: true,
        format,
        modelId: picked.modelId,
        poster: picked.poster,
        defaultCameraOrbit: picked.defaultCameraOrbit,
        defaultCameraTarget: picked.defaultCameraTarget,
        fieldOfView: picked.fieldOfView,
      } satisfies ThreeDContent,
      style: { borderRadius: 8 },
    });
  };

  return (
    <div className="space-y-4">
      <Tabs
        value={tab}
        onChange={(t) => setTab(t as 'library' | 'url' | 'upload')}
        options={[
          { id: 'library', label: 'From library' },
          { id: 'url', label: 'From URL' },
          { id: 'upload', label: 'Upload .glb / .gltf' },
        ]}
      />

      {tab === 'library' ? (
        <ModelLibraryPicker selectedUrl={src} onSelect={selectAsset} />
      ) : tab === 'url' ? (
        <Input
          label="Model URL"
          autoFocus
          placeholder="https://…/model.glb"
          value={src.startsWith('data:') ? '' : src}
          onChange={(e) => {
            setSrc(e.target.value);
            setFilename(null);
            setPicked({});
          }}
        />
      ) : (
        <div>
          <input
            ref={fileRef}
            type="file"
            accept=".glb,.gltf,model/gltf-binary,model/gltf+json"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onFile(f);
            }}
          />
          <button
            type="button"
            disabled={loading}
            onClick={() => fileRef.current?.click()}
            className="flex h-32 w-full flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border bg-bg-subtle/40 text-fg-muted transition hover:border-accent/60 hover:text-accent disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Upload size={20} />
            <span className="text-sm font-medium">
              {loading ? 'Uploading…' : filename ? filename : 'Click to upload a 3D model'}
            </span>
            <span className="text-[11px] text-fg-subtle">
              .glb or .gltf — uploaded to S3
            </span>
          </button>
          {loading && <UploadProgressBar value={progress} />}
        </div>
      )}

      <Input
        label="Alt text (optional)"
        value={alt}
        onChange={(e) => setAlt(e.target.value)}
        placeholder="Describes the model for accessibility"
      />

      <label className="flex items-center gap-2 text-xs font-medium text-fg-muted">
        <input
          type="checkbox"
          checked={autoRotate}
          onChange={(e) => setAutoRotate(e.target.checked)}
          className="accent-accent"
        />
        Auto-rotate model
      </label>

      {error && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-600">{error}</p>
      )}

      <PreviewCard>
        {src ? (
          <ModelPreview
            src={src}
            alt={alt}
            autoRotate={autoRotate}
            onError={() => setError('Could not render this model. Check the URL or file.')}
          />
        ) : (
          <div className="flex h-32 items-center justify-center gap-2 text-xs text-fg-subtle">
            <Boxes size={20} /> Provide a model to preview
          </div>
        )}
      </PreviewCard>

      <FooterRow onCancel={onCancel} onConfirm={submit} disabled={!src} />
    </div>
  );
}

function ModelPreview({
  src,
  alt,
  autoRotate,
  onError,
}: {
  src: string;
  alt: string;
  autoRotate: boolean;
  onError: () => void;
}) {
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    setReady(false);
    import('@google/model-viewer')
      .then(() => {
        if (!cancelled) setReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setFailed(true);
        onError();
      });
    return () => {
      cancelled = true;
    };
  }, [onError]);

  if (failed)
    return (
      <div className="flex h-48 items-center justify-center text-xs text-red-500">
        Failed to load 3D viewer.
      </div>
    );

  if (!ready)
    return (
      <div className="flex h-48 items-center justify-center text-xs text-fg-subtle">
        Loading 3D viewer…
      </div>
    );

  return (
    <div className="h-56 w-full overflow-hidden rounded bg-gradient-to-br from-slate-100 to-slate-200">
      {/* @ts-expect-error -- model-viewer is a custom element registered at runtime */}
      <model-viewer
        src={src}
        alt={alt || '3D model preview'}
        camera-controls=""
        auto-rotate={autoRotate ? '' : undefined}
        shadow-intensity="1"
        interaction-prompt="none"
        style={{ width: '100%', height: '100%', backgroundColor: 'transparent' }}
        onError={onError}
      />
    </div>
  );
}

function ModelLibraryPicker({
  selectedUrl,
  onSelect,
}: {
  selectedUrl: string;
  onSelect: (asset: Asset) => void;
}) {
  const [assets, setAssets] = useState<Asset[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.assets
      .list({ kind: 'model3d' })
      .then((list) => {
        if (!cancelled) setAssets(list);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load library');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) return <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-600">{error}</p>;
  if (!assets) return <p className="text-xs text-fg-subtle">Loading library…</p>;
  if (assets.length === 0)
    return (
      <p className="rounded-md border border-dashed border-border px-3 py-6 text-center text-xs text-fg-subtle">
        No models in the library yet. Upload one here, or manage your library on the Assets page.
      </p>
    );

  return (
    <div className="grid max-h-64 grid-cols-3 gap-2 overflow-y-auto">
      {assets.map((asset) => (
        <button
          key={asset._id}
          type="button"
          onClick={() => onSelect(asset)}
          className={cn(
            'flex flex-col overflow-hidden rounded-md border text-left transition',
            asset.url === selectedUrl
              ? 'border-accent ring-2 ring-accent/30'
              : 'border-border hover:border-accent/60',
          )}
        >
          <div className="grid h-20 place-items-center bg-gradient-to-br from-slate-100 to-slate-200">
            {asset.poster ? (
              <img src={asset.poster} alt={asset.name} className="h-full w-full object-cover" />
            ) : (
              <Boxes size={20} className="text-indigo-500" />
            )}
          </div>
          <span className="truncate px-2 py-1 text-[11px] font-medium text-fg" title={asset.name}>
            {asset.name}
          </span>
        </button>
      ))}
    </div>
  );
}

// ---------------- helpers ----------------

interface ChildProps {
  onCancel: () => void;
  onConfirm: (element: ScriptElement) => void;
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium text-fg-muted">{label}</span>
      <div className="flex h-9 items-center gap-1.5 rounded-md border border-border bg-white pl-1.5 pr-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-6 w-7 cursor-pointer rounded-sm border border-border"
        />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent text-xs text-fg outline-none"
        />
      </div>
    </label>
  );
}

// ---------------- LATEX ----------------

const LATEX_SAMPLES: { label: string; source: string; displayMode: boolean }[] = [
  { label: 'Pythagoras', source: 'a^2 + b^2 = c^2', displayMode: true },
  { label: 'Quadratic', source: 'x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}', displayMode: true },
  { label: 'Integral', source: '\\int_{0}^{\\pi} \\sin(x)\\,dx = 2', displayMode: true },
  { label: 'Sum', source: '\\sum_{i=1}^{n} i = \\frac{n(n+1)}{2}', displayMode: true },
  { label: 'Inline frac', source: '\\frac{1}{2}', displayMode: false },
];

/** A single insertable snippet shown on the LaTeX toolbar.
 *  `snippet` may include `$1`, `$2`, … placeholders. When the user has a
 *  selection in the textarea, the selected text replaces `$1`. Cursor lands
 *  in the first empty placeholder after insertion. */
interface LatexSnippet {
  /** What's painted on the button — usually a rendered glyph. */
  label: string;
  /** Raw TeX with `$1`/`$2` placeholders. */
  snippet: string;
  /** Tooltip / accessible name. */
  title?: string;
  /** Slightly larger button (used for big operators like ∫ ∑). */
  wide?: boolean;
}

interface LatexToolbarTab {
  id: string;
  label: string;
  items: LatexSnippet[];
}

const LATEX_TOOLBAR: LatexToolbarTab[] = [
  {
    id: 'common',
    label: 'Common',
    items: [
      { label: 'a/b',  snippet: '\\frac{$1}{$2}',          title: 'Fraction' },
      { label: '√',    snippet: '\\sqrt{$1}',              title: 'Square root', wide: true },
      { label: 'ⁿ√',   snippet: '\\sqrt[$1]{$2}',          title: 'nth root' },
      { label: 'x²',   snippet: '$1^{2}',                  title: 'Squared' },
      { label: 'xⁿ',   snippet: '$1^{$2}',                 title: 'Superscript' },
      { label: 'xₙ',   snippet: '$1_{$2}',                 title: 'Subscript' },
      { label: '±',    snippet: '\\pm ',                   title: '\\pm' },
      { label: '·',    snippet: '\\cdot ',                 title: '\\cdot' },
      { label: '×',    snippet: '\\times ',                title: '\\times' },
      { label: '÷',    snippet: '\\div ',                  title: '\\div' },
      { label: '≤',    snippet: '\\le ',                   title: '\\le' },
      { label: '≥',    snippet: '\\ge ',                   title: '\\ge' },
      { label: '≠',    snippet: '\\ne ',                   title: '\\ne' },
      { label: '≈',    snippet: '\\approx ',               title: '\\approx' },
      { label: '∞',    snippet: '\\infty ',                title: '\\infty' },
      { label: '→',    snippet: '\\to ',                   title: '\\to' },
    ],
  },
  {
    id: 'calc',
    label: 'Calculus',
    items: [
      { label: '∫',     snippet: '\\int_{$1}^{$2} $3\\,d$4', title: 'Integral', wide: true },
      { label: '∬',     snippet: '\\iint_{$1} $2\\,dA',      title: 'Double integral', wide: true },
      { label: '∮',     snippet: '\\oint_{$1} $2',           title: 'Contour integral' },
      { label: '∑',     snippet: '\\sum_{$1=$2}^{$3} $4',    title: 'Summation', wide: true },
      { label: '∏',     snippet: '\\prod_{$1=$2}^{$3} $4',   title: 'Product', wide: true },
      { label: 'lim',   snippet: '\\lim_{$1 \\to $2} $3',    title: 'Limit', wide: true },
      { label: 'd/dx',  snippet: '\\frac{d}{d$1}\\,$2',      title: 'Derivative' },
      { label: '∂/∂x',  snippet: '\\frac{\\partial $1}{\\partial $2}', title: 'Partial derivative' },
      { label: '∂',     snippet: '\\partial ',               title: '\\partial' },
      { label: '∇',     snippet: '\\nabla ',                 title: '\\nabla' },
    ],
  },
  {
    id: 'greek',
    label: 'Greek',
    items: [
      { label: 'α', snippet: '\\alpha ', title: '\\alpha' },
      { label: 'β', snippet: '\\beta ',  title: '\\beta' },
      { label: 'γ', snippet: '\\gamma ', title: '\\gamma' },
      { label: 'δ', snippet: '\\delta ', title: '\\delta' },
      { label: 'ε', snippet: '\\epsilon ', title: '\\epsilon' },
      { label: 'ζ', snippet: '\\zeta ',  title: '\\zeta' },
      { label: 'η', snippet: '\\eta ',   title: '\\eta' },
      { label: 'θ', snippet: '\\theta ', title: '\\theta' },
      { label: 'κ', snippet: '\\kappa ', title: '\\kappa' },
      { label: 'λ', snippet: '\\lambda ', title: '\\lambda' },
      { label: 'μ', snippet: '\\mu ',    title: '\\mu' },
      { label: 'ν', snippet: '\\nu ',    title: '\\nu' },
      { label: 'ξ', snippet: '\\xi ',    title: '\\xi' },
      { label: 'π', snippet: '\\pi ',    title: '\\pi' },
      { label: 'ρ', snippet: '\\rho ',   title: '\\rho' },
      { label: 'σ', snippet: '\\sigma ', title: '\\sigma' },
      { label: 'τ', snippet: '\\tau ',   title: '\\tau' },
      { label: 'φ', snippet: '\\phi ',   title: '\\phi' },
      { label: 'χ', snippet: '\\chi ',   title: '\\chi' },
      { label: 'ψ', snippet: '\\psi ',   title: '\\psi' },
      { label: 'ω', snippet: '\\omega ', title: '\\omega' },
      { label: 'Γ', snippet: '\\Gamma ', title: '\\Gamma' },
      { label: 'Δ', snippet: '\\Delta ', title: '\\Delta' },
      { label: 'Θ', snippet: '\\Theta ', title: '\\Theta' },
      { label: 'Λ', snippet: '\\Lambda ', title: '\\Lambda' },
      { label: 'Ξ', snippet: '\\Xi ',    title: '\\Xi' },
      { label: 'Π', snippet: '\\Pi ',    title: '\\Pi' },
      { label: 'Σ', snippet: '\\Sigma ', title: '\\Sigma' },
      { label: 'Φ', snippet: '\\Phi ',   title: '\\Phi' },
      { label: 'Ψ', snippet: '\\Psi ',   title: '\\Psi' },
      { label: 'Ω', snippet: '\\Omega ', title: '\\Omega' },
    ],
  },
  {
    id: 'sets',
    label: 'Sets',
    items: [
      { label: '∈',  snippet: '\\in ',         title: '\\in' },
      { label: '∉',  snippet: '\\notin ',      title: '\\notin' },
      { label: '⊂',  snippet: '\\subset ',     title: '\\subset' },
      { label: '⊆',  snippet: '\\subseteq ',   title: '\\subseteq' },
      { label: '∪',  snippet: '\\cup ',        title: '\\cup' },
      { label: '∩',  snippet: '\\cap ',        title: '\\cap' },
      { label: '∅',  snippet: '\\emptyset ',   title: '\\emptyset' },
      { label: '∀',  snippet: '\\forall ',     title: '\\forall' },
      { label: '∃',  snippet: '\\exists ',     title: '\\exists' },
      { label: 'ℝ',  snippet: '\\mathbb{R}',   title: 'Real numbers' },
      { label: 'ℕ',  snippet: '\\mathbb{N}',   title: 'Natural numbers' },
      { label: 'ℤ',  snippet: '\\mathbb{Z}',   title: 'Integers' },
      { label: 'ℚ',  snippet: '\\mathbb{Q}',   title: 'Rationals' },
      { label: 'ℂ',  snippet: '\\mathbb{C}',   title: 'Complex numbers' },
    ],
  },
  {
    id: 'brackets',
    label: 'Brackets',
    items: [
      { label: '( )',     snippet: '\\left( $1 \\right)',  title: 'Auto-sized parens' },
      { label: '[ ]',     snippet: '\\left[ $1 \\right]',  title: 'Square brackets' },
      { label: '{ }',     snippet: '\\left\\{ $1 \\right\\}', title: 'Curly braces' },
      { label: '⟨ ⟩',     snippet: '\\langle $1 \\rangle', title: 'Angle brackets' },
      { label: '| |',     snippet: '\\left| $1 \\right|',  title: 'Absolute value' },
      { label: '⌊ ⌋',     snippet: '\\lfloor $1 \\rfloor', title: 'Floor' },
      { label: '⌈ ⌉',     snippet: '\\lceil $1 \\rceil',   title: 'Ceiling' },
    ],
  },
  {
    id: 'matrix',
    label: 'Matrix',
    items: [
      {
        label: '2×2',
        snippet: '\\begin{pmatrix} $1 & $2 \\\\ $3 & $4 \\end{pmatrix}',
        title: '2×2 matrix',
        wide: true,
      },
      {
        label: '3×3',
        snippet:
          '\\begin{pmatrix} $1 & $2 & $3 \\\\ $4 & $5 & $6 \\\\ $7 & $8 & $9 \\end{pmatrix}',
        title: '3×3 matrix',
        wide: true,
      },
      {
        label: '[ ]',
        snippet: '\\begin{bmatrix} $1 & $2 \\\\ $3 & $4 \\end{bmatrix}',
        title: 'Bracket matrix',
        wide: true,
      },
      {
        label: 'cases',
        snippet: '\\begin{cases} $1 & \\text{if } $2 \\\\ $3 & \\text{otherwise} \\end{cases}',
        title: 'Cases / piecewise',
        wide: true,
      },
    ],
  },
];

/**
 * Validate a TeX source by attempting to render it. Returns `null` on
 * success, or the parser's error message on failure. Used to surface the
 * exact KaTeX error inline below the editor without crashing the preview.
 */
function validateLatex(source: string): string | null {
  if (!source.trim()) return null;
  try {
    katex.renderToString(source, { throwOnError: true, strict: 'ignore' });
    return null;
  } catch (err) {
    return (err as Error).message;
  }
}

/**
 * Apply a snippet to the textarea state. Handles `$1..$N` placeholders, and
 * if there is an active selection the selected text replaces `$1`. Cursor
 * is positioned at the first remaining empty placeholder so the user can
 * keep typing immediately.
 */
function expandSnippet(
  snippet: string,
  selection: string,
): { text: string; cursor: number } {
  let cursor: number | null = null;
  let placeholderUsed = false;

  // Walk `$N` tokens left-to-right. The first one consumes the selection
  // (when there is one); subsequent ones become empty and the very first
  // empty placeholder is where we drop the cursor.
  const text = snippet.replace(/\$(\d+)/g, (_, n: string) => {
    const isFirst = n === '1';
    if (isFirst && selection && !placeholderUsed) {
      placeholderUsed = true;
      return selection;
    }
    if (cursor === null) {
      // We don't know our absolute offset yet — use a sentinel and resolve
      // after the full string is built.
      cursor = -1;
    }
    return '';
  });

  // Resolve cursor to an absolute offset: if a placeholder was emptied,
  // place cursor at its location (re-run the regex to find the first
  // resulting "gap" by reconstructing). Easier: scan the original snippet
  // to find the first $N that didn't consume the selection.
  if (cursor !== null) {
    let i = 0;
    let outOffset = 0;
    let found = -1;
    while (i < snippet.length) {
      const m = /^\$(\d+)/.exec(snippet.slice(i));
      if (m) {
        const isFirst = m[1] === '1';
        if (isFirst && selection) {
          outOffset += selection.length;
        } else if (found === -1) {
          found = outOffset;
        }
        i += m[0].length;
        continue;
      }
      outOffset += 1;
      i += 1;
    }
    cursor = found === -1 ? text.length : found;
  } else {
    cursor = text.length;
  }

  return { text, cursor };
}

function LatexCreator({ onCancel, onConfirm }: ChildProps) {
  const [source, setSource] = useState<string>('E = mc^2');
  const [displayMode, setDisplayMode] = useState(true);
  const [tab, setTab] = useState<string>('common');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const error = useMemo(() => validateLatex(source), [source]);
  const activeTab = LATEX_TOOLBAR.find((t) => t.id === tab) ?? LATEX_TOOLBAR[0];

  const insertSnippet = (snippet: LatexSnippet) => {
    const ta = textareaRef.current;
    const start = ta?.selectionStart ?? source.length;
    const end = ta?.selectionEnd ?? source.length;
    const selected = source.slice(start, end);

    const { text: expanded, cursor } = expandSnippet(snippet.snippet, selected);
    const next = source.slice(0, start) + expanded + source.slice(end);
    setSource(next);

    // Restore focus and place cursor inside the first placeholder on the
    // next paint so React has flushed the new value.
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      const pos = start + cursor;
      el.setSelectionRange(pos, pos);
    });
  };

  const submit = () => {
    if (!source.trim() || error) return;
    onConfirm({
      id: uid('el'),
      type: 'latex',
      content: { source, displayMode } satisfies LatexContent,
      style: { fontSize: 18, color: '#1f2937' },
    });
  };

  return (
    <div className="space-y-4">
      {/* Toolbar tabs */}
      <div className="rounded-md border border-border bg-bg-subtle/40">
        <div className="flex flex-wrap gap-1 border-b border-border-subtle px-1.5 py-1">
          {LATEX_TOOLBAR.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`rounded px-2.5 py-1 text-[11px] font-medium transition ${
                tab === t.id
                  ? 'bg-white text-fg shadow-sm border border-border'
                  : 'text-fg-muted hover:text-fg'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-1 px-2 py-2">
          {activeTab.items.map((s, i) => (
            <button
              key={`${activeTab.id}-${i}`}
              type="button"
              title={s.title ?? s.snippet}
              onClick={() => insertSnippet(s)}
              className={`grid place-items-center rounded-md border border-border bg-white text-[13px] font-medium text-fg-muted transition hover:border-accent/40 hover:bg-accent-soft/40 hover:text-fg ${
                s.wide ? 'h-8 px-2.5 min-w-[40px]' : 'h-8 w-8'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <label className="block">
        <span className="mb-1 flex items-center justify-between">
          <span className="text-[11px] font-medium text-fg-muted">
            TeX / LaTeX source
          </span>
          <span className="text-[10px] text-fg-subtle">
            Tip: select text, then click a button to wrap it.
          </span>
        </span>
        <textarea
          ref={textareaRef}
          value={source}
          onChange={(e) => setSource(e.target.value)}
          rows={4}
          spellCheck={false}
          placeholder="e.g.  \\frac{a}{b} + \\sqrt{x^2 + 1}"
          className={`block w-full resize-y rounded-md border bg-white px-3 py-2 font-mono text-xs text-fg outline-none focus:ring-2 ${
            error
              ? 'border-red-300 focus:border-red-400 focus:ring-red-200'
              : 'border-border focus:border-accent focus:ring-accent/20'
          }`}
        />
      </label>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11.5px] text-red-700">
          <span className="font-semibold">Parse error:</span>{' '}
          <span className="font-mono">{error}</span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <Tabs
          value={displayMode ? 'block' : 'inline'}
          onChange={(id) => setDisplayMode(id === 'block')}
          options={[
            { id: 'block', label: 'Block (centered)' },
            { id: 'inline', label: 'Inline' },
          ]}
        />
        <span className="text-[11px] text-fg-subtle">Powered by KaTeX</span>
      </div>

      <PreviewCard>
        {source.trim() ? (
          <div className="flex min-h-28 w-full items-center justify-center px-4 py-4">
            <LatexElement
              element={{
                id: 'preview',
                type: 'latex',
                content: { source, displayMode },
                style: { fontSize: 22, color: '#1f2937' },
              }}
            />
          </div>
        ) : (
          <div className="flex h-24 items-center justify-center text-xs text-fg-subtle">
            Type an expression to see a live preview
          </div>
        )}
      </PreviewCard>

      <div>
        <span className="mb-1 block text-[11px] font-medium text-fg-muted">Quick samples</span>
        <div className="flex flex-wrap gap-1.5">
          {LATEX_SAMPLES.map((s) => (
            <button
              key={s.label}
              onClick={() => {
                setSource(s.source);
                setDisplayMode(s.displayMode);
              }}
              className="rounded-md border border-border bg-white px-2 py-1 text-[11px] font-medium text-fg-muted transition hover:border-accent/40 hover:text-fg"
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <FooterRow
        onCancel={onCancel}
        onConfirm={submit}
        disabled={!source.trim() || Boolean(error)}
      />
    </div>
  );
}

function Tabs({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (id: string) => void;
  options: { id: string; label: string }[];
}) {
  return (
    <div className="inline-flex rounded-md border border-border bg-bg-subtle p-0.5">
      {options.map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          className={cn(
            'h-7 rounded px-3 text-[12px] font-medium transition',
            value === o.id ? 'bg-white text-fg shadow-pill' : 'text-fg-muted hover:text-fg',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function stripExtensionForLabel(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
}

// ---------------- ICON ----------------

function IconCreator({ onCancel, onConfirm }: ChildProps) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState('idea');
  const results = searchIcons(query).slice(0, 24);

  const submit = () => {
    onConfirm({ id: uid('el'), type: 'icon', content: { id: selected, size: 64 }, style: { color: '#6366f1' } });
  };

  return (
    <div className="space-y-3">
      <input
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search icons…"
        className="h-9 w-full rounded-md border border-border bg-white px-3 text-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
      />
      <div className="grid grid-cols-6 gap-2">
        {results.map((i) => (
          <button
            key={i.id}
            title={i.name}
            onClick={() => setSelected(i.id)}
            className={cn(
              'flex aspect-square items-center justify-center rounded-md border transition hover:bg-bg-subtle',
              selected === i.id ? 'border-accent bg-accent-soft/40 text-accent' : 'border-border text-fg-muted',
            )}
          >
            <i.Glyph size={20} />
          </button>
        ))}
      </div>
      <FooterRow onCancel={onCancel} onConfirm={submit} />
    </div>
  );
}

// ---------------- CALLOUT ----------------

function CalloutCreator({ onCancel, onConfirm }: ChildProps) {
  const [variant, setVariant] = useState<CalloutVariant>('important_concept');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  const submit = () => {
    onConfirm({
      id: uid('el'),
      type: 'callout',
      content: { variant, title: title.trim() || undefined, body: body.trim() || CALLOUT_BODY_PLACEHOLDERS[variant] },
      style: { fontSize: 14, color: '#1f2937' },
    });
  };

  return (
    <div className="space-y-3">
      <Select label="Variant" value={variant} onChange={(e) => setVariant(e.target.value as CalloutVariant)}>
        {CALLOUT_VARIANTS.map((v) => (
          <option key={v} value={v}>
            {CALLOUT_VARIANT_LABELS[v]}
          </option>
        ))}
      </Select>
      <Input label="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} />
      <label className="block">
        <span className="mb-1 block text-[11px] font-medium text-fg-muted">Body</span>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          placeholder={CALLOUT_BODY_PLACEHOLDERS[variant]}
          className="w-full rounded-md border border-border bg-white p-2 text-sm text-fg focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
        />
      </label>
      <FooterRow onCancel={onCancel} onConfirm={submit} />
    </div>
  );
}

// ---------------- MERMAID ----------------

function MermaidCreator({ onCancel, onConfirm }: ChildProps) {
  const [diagramType, setDiagramType] = useState<MermaidDiagramType>('flowchart-td');
  const [source, setSource] = useState(DEFAULT_MERMAID_SOURCE);

  const onType = (t: MermaidDiagramType) => {
    setDiagramType(t);
    setSource((prev) => withDiagramType(prev, t));
  };

  const submit = () => {
    onConfirm({
      id: uid('el'),
      type: 'mermaid',
      content: { source, diagramType },
      style: {},
    });
  };

  return (
    <div className="space-y-3">
      <Select
        label="Diagram type"
        value={diagramType}
        onChange={(e) => onType(e.target.value as MermaidDiagramType)}
      >
        {MERMAID_DIAGRAM_TYPES.map((d) => (
          <option key={d.value} value={d.value}>
            {d.label}
          </option>
        ))}
      </Select>
      <label className="block">
        <span className="mb-1 block text-[11px] font-medium text-fg-muted">Diagram source</span>
        <textarea
          value={source}
          onChange={(e) => setSource(e.target.value)}
          rows={6}
          spellCheck={false}
          className="block w-full resize-y rounded-md border border-border bg-white px-2 py-1.5 font-mono text-xs text-fg outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
        />
      </label>
      <PreviewCard>
        <div className="h-40">
          <MermaidElement element={{ id: 'preview', type: 'mermaid', content: { source, diagramType } }} fill />
        </div>
      </PreviewCard>
      <FooterRow onCancel={onCancel} onConfirm={submit} />
    </div>
  );
}
